/**
 * Generate a url for a token's svg leveraging the Morpho CDN.
 *
 * @export
 * @param {string} symbol
 * @returns {string} url of token svg resource
 */
export function getTokenIconUrl(symbol: string) {
  return `https://cdn.morpho.org/assets/logos/${encodeURIComponent(symbol.toLowerCase())}.svg`
}

const LOGO_PROXY_PATH = '/api/logo'

/**
 * Many cdn.morpho.org `.svg` logos are raster-in-SVG wrappers too heavy for the
 * browser; rewrite them to the app-relative logo proxy so next/image can
 * optimize the rasterized PNG. Anything else is returned unchanged.
 * Only use in apps that mount `createLogoRoute` from `@repo/logo-proxy` at
 * `LOGO_PROXY_PATH`; the hostname here must match that route's `allowedOrigins`.
 */
export function toLogoSrc(src: string): string {
  let url: URL
  try {
    url = new URL(src)
  } catch {
    return src
  }
  if (url.protocol !== 'https:' || url.hostname !== 'cdn.morpho.org') return src
  if (!url.pathname.endsWith('.svg')) return src
  return `${LOGO_PROXY_PATH}?url=${encodeURIComponent(src)}`
}

/**
 * Quality the logo proxy loader always requests. Next maps it to AVIF quality 63;
 * at the default 75 (AVIF 47) thin rings and text in logos blur. Must be listed
 * in the app's `images.qualities`, or the optimizer returns 400.
 */
const LOGO_PROXY_QUALITY = 100

/**
 * next/image loader for `toLogoSrc` paths. Next's default loader appends
 * `&dpl=<deployment id>` to app-relative srcs, which gives every logo a new URL
 * on each release and makes browsers download it again. Vercel's image cache
 * ignores `dpl`, so dropping it only keeps the browser cache. Bump a version
 * query on the logo URL when the proxy's output changes.
 */
export function logoProxyLoader({ src, width }: { src: string; width: number }): string {
  return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=${LOGO_PROXY_QUALITY}`
}

export function isLogoProxySrc(src: string): boolean {
  return src.startsWith(`${LOGO_PROXY_PATH}?`)
}
